/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useState, useCallback, useRef } from 'react';
import { Pressable, View } from 'react-native';
import ListView from '@airasia-phoenix/react-native/components/ListView';
import DividerView from '@airasia-phoenix/react-native/components/DividerView/index';
import {
  DividerDirection,
  DividerType,
} from '@airasia-phoenix/react-native/components/DividerView/types';
import IconView from '@airasia-phoenix/react-native/components/IconView/index';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import Language from '@move-travel/react-native-phoenix-icons/Language';
import MonetizationOn from '@move-travel/react-native-phoenix-icons/MonetizationOn';
import ArrowDropDown from '@move-travel/react-native-phoenix-icons/ArrowDropDown';
import ChevronRight from '@move-travel/react-native-phoenix-icons/ChevronRight';

import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { pd } from '@airasia-phoenix/react-native/tokens';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { styles } from './styles';

export interface LocaleCurrencySelectorProps {
  currencyText?: string;
  languageText?: string;
  onCurrencyPress?: () => void;
  onLanguagePress?: () => void;
  isMobile?: boolean;
}

const LocaleCurrencySelector: React.FC<LocaleCurrencySelectorProps> = ({
  currencyText,
  languageText,
  onCurrencyPress,
  onLanguagePress,
  isMobile = false,
}) => {
  const [pickerVisible, setPickerVisible] = useState(false);
  const wrapperRef = useRef<React.ComponentRef<typeof View>>(null);
  const mobileLocaleCurrencyText = [currencyText, languageText]
    .filter((text): text is string => Boolean(text))
    .join(' | ');

  const closePicker = useCallback(() => {
    setPickerVisible(false);
  }, []);

  useOutsideClick(wrapperRef, closePicker, { enabled: pickerVisible });

  const togglePicker = useCallback(() => {
    setPickerVisible((visible) => !visible);
  }, []);

  const handleCurrencyPick = useCallback(() => {
    setPickerVisible(false);
    onCurrencyPress?.();
  }, [onCurrencyPress]);

  const handleLanguagePick = useCallback(() => {
    setPickerVisible(false);
    onLanguagePress?.();
  }, [onLanguagePress]);

  if (!currencyText && !languageText) return null;

  if (isMobile) {
    return (
      <View ref={wrapperRef} style={styles.mobileLocaleCurrencyWrapper}>
        <Pressable onPress={togglePicker} style={styles.mobileLocaleCurrency}>
          <TextView
            text={mobileLocaleCurrencyText}
            numberOfLines={1}
            style={getTextStyle(
              pd.sys.typescale.sub_body_text,
              pd.sys.colour.content_primary,
            )}
          />
          <IconView
            height={24}
            width={24}
            tint={pd.sys.colour.content_primary.toString()}
          >
            <ArrowDropDown />
          </IconView>
        </Pressable>

        {pickerVisible && (
          <View style={styles.pickerContainer}>
            {currencyText && (
              <ListView
                primaryText={currencyText}
                startElement={{
                  customElement: (
                    <IconView
                      height={24}
                      width={24}
                      tint={pd.sys.colour.content_primary.toString()}
                    >
                      <MonetizationOn />
                    </IconView>
                  ),
                }}
                endElement={{
                  customElement: (
                    <IconView
                      height={24}
                      width={24}
                      tint={pd.sys.colour.content_primary.toString()}
                    >
                      <ChevronRight />
                    </IconView>
                  ),
                }}
                onPress={handleCurrencyPick}
              />
            )}
            {languageText && (
              <ListView
                primaryText={languageText}
                startElement={{
                  customElement: (
                    <IconView
                      height={24}
                      width={24}
                      tint={pd.sys.colour.content_primary.toString()}
                    >
                      <Language />
                    </IconView>
                  ),
                }}
                endElement={{
                  customElement: (
                    <IconView
                      height={24}
                      width={24}
                      tint={pd.sys.colour.content_primary.toString()}
                    >
                      <ChevronRight />
                    </IconView>
                  ),
                }}
                onPress={handleLanguagePick}
              />
            )}
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={styles.localeCurrencySplit}>
      {currencyText && (
        <Pressable
          onPress={onCurrencyPress}
          style={styles.localeCurrencySplitBtn}
        >
          <IconView
            height={16}
            width={16}
            tint={pd.sys.colour.content_primary.toString()}
            children={<MonetizationOn />}
          />
          <TextView
            text={currencyText}
            style={getTextStyle(
              pd.sys.typescale.sub_body_text,
              pd.sys.colour.content_primary,
            )}
          />
        </Pressable>
      )}
      {currencyText && languageText && (
        <DividerView
          type={DividerType.Solid}
          direction={DividerDirection.Vertical}
          thickness={1}
          height={16}
        />
      )}
      {languageText && (
        <Pressable
          onPress={onLanguagePress}
          style={styles.localeCurrencySplitBtn}
        >
          <IconView
            height={16}
            width={16}
            tint={pd.sys.colour.content_primary.toString()}
          >
            <Language />
          </IconView>
          <TextView
            text={languageText}
            style={getTextStyle(
              pd.sys.typescale.sub_body_text,
              pd.sys.colour.content_primary,
            )}
          />
        </Pressable>
      )}
    </View>
  );
};

export default LocaleCurrencySelector;
